import { componentWrapper } from "../../utilities";
import type {
    CustomAutoMenuFactory,
    IAutoMenuElement,
    IAutoMenuItem,
    IAutoMenuWithPlainItemsProps,
    IProps,
} from "./auto-menu.types";
import { Menu } from "../menu";
import { cloneElement, isValidElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../icon";
import { Text } from "../text";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { kebabCase } from "lodash";
import { Highlight } from "../highlight";
import { Group } from "../group";
import { ScrollArea } from "../scroll-area";
import { Stack } from "../stack";
import {
    DEFAULT_SEARCH_PLACEHOLDER,
    MENU_AVAILABLE_HEIGHT,
    MENU_DROPDOWN_SELECTOR,
    SCROLLBAR_SELECTOR,
    getRowsHeight,
    ROW_LABEL_SIZE,
    SEARCHABLE_MENU_MAX_HEIGHT,
    SEARCHABLE_MENU_VISIBLE_ROWS,
    SEARCHABLE_SUB_MENU_MAX_HEIGHT,
} from "./auto-menu.constants";

/**
 * Whether an element takes text input of its own. Menus can hold arbitrary elements, so the type-to-search
 * handler has to leave anything typeable alone rather than pulling those keystrokes into the search box.
 * @param target The element the key event came from.
 * @returns True when the element handles its own typing.
 */
const isEditableElement = (target: EventTarget | null): boolean => {
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
    );
};

/**
 * AutoMenu component for automatically generating menu structures from item arrays.
 * @param {React.ReactNode} props.children - The trigger element that opens the menu.
 * @param {Array} props.items - Array of menu items to display.
 * @param {string} props.title - Optional title to display in the menu.
 * @param {string} props.subtitle - Optional subtitle to display in the menu.
 * @param {function} props.onClick - Callback function when a menu item is clicked.
 * @param {boolean} props.searchable - Whether the menu should have a search input.
 * @param {function} props.onChange - Callback function when the menu state changes.
 * @param {boolean} props.hasCustomSearch - Whether the menu uses a custom search implementation.
 * @param {string} props.noItemsFoundLabel - Label to show when no items match the search.
 * @param {string | number} props.maxMenuHeight - Maximum height for the menu.
 * @param {string | number} props.maxSubMenuHeight - Maximum height for the category sub-menus.
 * @param {string} props.subMenuFloatingStrategy - How the category sub-menus are positioned (see its type).
 * @param {React.ReactNode} props.header - Guidance shown at the top of the menu, above the search.
 * @param {string} props.initialSearch - Seeds the search when a controlled menu opens, and when it changes while open.
 * @param {function} props.onSearchChange - Called with the search after every change.
 * @param {string} props.searchPlaceholder - The search input's placeholder.
 * @returns {JSX.Element} The AutoMenu component.
 */
const AutoMenu = componentWrapper<CustomAutoMenuFactory, IProps>(
    "Menu",
    (
        {
            children,
            items,
            title,
            subtitle,
            onClick,
            searchable,
            onChange,
            hasCustomSearch,
            noItemsFoundLabel,
            "data-testid": dataTestId,
            groupType = "sub-menu",
            highlightedText,
            maxMenuHeight: maxMenuHeightProp,
            maxSubMenuHeight: maxSubMenuHeightProp,
            subMenuFloatingStrategy: subMenuFloatingStrategyProp,
            header,
            initialSearch,
            onSearchChange,
            searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
            opened,
            ...other
        },
        ref
    ) => {
        // A searchable menu is capped at a set number of rows, and its sub-menus with it, unless the consumer says
        // otherwise. Its sub-menus are then positioned against the viewport, so the list's scroll area doesn't clip them.
        const maxMenuHeight = maxMenuHeightProp ?? (searchable ? SEARCHABLE_MENU_MAX_HEIGHT : undefined);
        const maxSubMenuHeight = maxSubMenuHeightProp ?? (searchable ? SEARCHABLE_SUB_MENU_MAX_HEIGHT : undefined);
        const subMenuFloatingStrategy = subMenuFloatingStrategyProp ?? (searchable ? "fixed" : undefined);
        const inputRef = useRef<HTMLInputElement | null>(null);
        // Only a controlled menu that is already open is seeded at mount; an uncontrolled one (no `opened`) always
        // starts empty, whatever `initialSearch` says, matching the prop's own contract.
        const [search, setSearch] = useState(opened === true ? (initialSearch ?? "") : "");
        // The category sub-menu held open while the pointer crosses the list's scrollbar (see below), or null.
        const [heldSubMenu, setHeldSubMenu] = useState<string | null>(null);

        /**
         * Holds a category's sub-menu open while the pointer crosses its list's scrollbar on the way to it. The
         * scrollbar overlays the rows' right edge, between a row and its sub-menu, so the pointer leaves the row before
         * it reaches the sub-menu, and Mantine closes a sub-menu as soon as the pointer leaves its row. Releasing the
         * hold when the pointer leaves the scrollbar hands the sub-menu back to Mantine: open if the pointer went on
         * into it, closed otherwise. Pressing on the scrollbar, to drag it, releases the hold too. Anywhere else, a
         * sub-menu closes at once.
         * @param subMenuId The sub-menu's id.
         * @param event     The pointer leaving the sub-menu's row.
         */
        const holdSubMenuAcrossScrollbar = useCallback((subMenuId: string, event: React.MouseEvent<HTMLElement>) => {
            // The native event's: React reports the nearest element it rendered instead of the one the pointer went to.
            const { relatedTarget } = event.nativeEvent;
            const scrollbar = relatedTarget instanceof Element ? relatedTarget.closest(SCROLLBAR_SELECTOR) : null;
            // Only the scrollbar of the list the row is in: a sub-menu's own scrollbar is somewhere else entirely.
            if (
                scrollbar === null ||
                scrollbar.closest(MENU_DROPDOWN_SELECTOR) !== event.currentTarget.closest(MENU_DROPDOWN_SELECTOR)
            ) {
                return;
            }
            setHeldSubMenu(subMenuId);
            const listeners = new AbortController();
            const release = () => {
                listeners.abort();
                setHeldSubMenu((held) => (held === subMenuId ? null : held));
            };
            scrollbar.addEventListener("mouseleave", release, { signal: listeners.signal });
            scrollbar.addEventListener("mousedown", release, { signal: listeners.signal });
        }, []);
        // Keeps the latest onSearchChange without making the reporting effect below re-run when only the
        // callback's identity changes, e.g. a consumer passing a new inline function on every render.
        const onSearchChangeRef = useRef(onSearchChange);

        /**
         * Send typing anywhere in the menu to the search box. Once the user has arrowed down into the list, focus
         * sits on an item and typing would otherwise go nowhere, so characters are appended to the search and
         * backspace erases from it, letting the user move between browsing and filtering without the mouse. The
         * text is edited here rather than left to the browser to deliver to the newly focused input, which is
         * timing dependent across browsers. Space is left alone because it activates the focused item.
         * @param event The keyboard event from anywhere inside the menu.
         */
        const handleTypeToSearch = useCallback(
            (event: React.KeyboardEvent) => {
                const isCharacter = event.key.length === 1 && event.key !== " ";
                const isBackspace = event.key === "Backspace";
                const hasModifier = event.ctrlKey || event.metaKey || event.altKey;

                if (!searchable || hasModifier || isEditableElement(event.target)) {
                    return;
                }

                if (!isCharacter && !isBackspace) {
                    return;
                }

                event.preventDefault();
                inputRef.current?.focus();
                setSearch((currentSearch) => (isBackspace ? currentSearch.slice(0, -1) : currentSearch + event.key));
            },
            [searchable]
        );

        // Seed the search each time a controlled menu opens, and again when the seed changes while it is open. The
        // `@` mount passes the text typed after `@`, which can still grow until focus has moved into the menu.
        //
        // Focus itself is not this effect's job on a fresh open: the search input carries `data-autofocus` below,
        // and `withInitialFocusPlaceholder={false}` on the returned `Menu` (further down) asks Mantine's own focus
        // trap to focus the search on mount instead of its usual placeholder. That trap fires from the dropdown's
        // own mount, so it works whichever tick the dropdown actually mounts on (immediately in this package's
        // jsdom tests, after the enter transition in a real browser) without AutoMenu racing it with a timer.
        // What the trap does *not* do is refire when `initialSearch` changes while already open — the dropdown
        // does not remount for that — so this effect focuses the input itself for that one case; on a fresh open
        // the input does not exist yet when this runs, so the call below is a harmless no-op and the trap alone
        // is what focuses it.
        useEffect(() => {
            if (opened !== true || typeof initialSearch === "undefined") {
                return;
            }
            setSearch(initialSearch);
            inputRef.current?.focus({ preventScroll: true });
        }, [opened, initialSearch]);

        // Keep the ref pointed at the latest onSearchChange, so the reporting effect below can stay subscribed to
        // only `search` and not fire again when just the callback's identity changes.
        useEffect(() => {
            onSearchChangeRef.current = onSearchChange;
        }, [onSearchChange]);

        // Report the search after every change to it: typing, type-to-search, a seed (including the one a
        // controlled menu mounts already open with, since that counts as a change from nothing to seeded), or
        // AutoMenu's own reset of the search when the menu closes. A consumer that passes its own `onClose`
        // replaces that reset (see the returned `Menu` below), so this effect does not see a change from it in
        // that case. Runs once more than the list above implies in React's Strict Mode, which double-invokes
        // effects in development; harmless here, since it reports the same, unchanged value.
        useEffect(() => {
            onSearchChangeRef.current?.(search);
        }, [search]);

        const internalHighlightedText = useMemo(() => {
            return highlightedText || search || "";
        }, [search, highlightedText]);

        const usedGroupType = useMemo(() => {
            return search.length > 0 ? "label" : groupType;
        }, [search.length, groupType]);

        const buildMenu = useCallback(
            (item: IAutoMenuElement, key: string, index?: number): ReactNode => {
                if (isValidElement(item)) {
                    return cloneElement(item, { key });
                }
                if ("label" in item) {
                    return <Menu.Label key={item.label}>{item.label}</Menu.Label>;
                }
                if ("items" in item && typeof item.items !== "undefined") {
                    if (usedGroupType === "label") {
                        // Match on the group name too, so searching a category shows all of its items even when
                        // none of their own names contain the search.
                        const filteredSubitems =
                            searchable && search.length > 0
                                ? item.items.filter((subItem) =>
                                      `${item.text} / ${subItem.text}`.toLowerCase().includes(search.toLowerCase())
                                  )
                                : item.items;
                        if (filteredSubitems.length === 0) {
                            return null;
                        }
                        const children = [
                            <Menu.Label key={item.text}>
                                <Group gap="xs">
                                    {item.icon && <Icon icon={item.icon} />}
                                    <Highlight inherit color="blue" highlight={internalHighlightedText}>
                                        {item.text}
                                    </Highlight>
                                </Group>
                            </Menu.Label>,
                            ...filteredSubitems.map((subItem, index) => {
                                return (
                                    <Menu.Item
                                        onClick={(e) => {
                                            onClick?.(subItem.value ?? subItem.text);
                                            subItem.menuItemProps?.onClick?.(e);
                                        }}
                                        value={subItem.value ?? subItem.text}
                                        active={subItem.active}
                                        data-testid={`menu-item-${kebabCase(subItem.text)}`}
                                        key={`${key}-${index}`}
                                        {...subItem.menuItemProps}
                                    >
                                        <Highlight
                                            size={ROW_LABEL_SIZE}
                                            color="blue"
                                            highlight={internalHighlightedText}
                                        >
                                            {subItem.text}
                                        </Highlight>
                                    </Menu.Item>
                                );
                            }),
                        ];
                        // Add a horizontal line after the group unless it's the last group
                        const lastGroupIndex = (() => {
                            for (let i = items.length - 1; i >= 0; i--) {
                                const maybeGroup = items[i];
                                if (
                                    !isValidElement(maybeGroup) &&
                                    "items" in maybeGroup &&
                                    typeof maybeGroup.items !== "undefined"
                                )
                                    return i;
                            }
                            return -1;
                        })();
                        if (typeof index === "number" && index !== lastGroupIndex) {
                            children.push(<Menu.Divider key={`${key}-divider`} />);
                        }
                        return children;
                    }
                    if (usedGroupType === "line") {
                        const children = item.items.map((subItem, index) => {
                            return (
                                <Menu.Item
                                    onClick={(e) => {
                                        onClick?.(subItem.value ?? subItem.text);
                                        subItem.menuItemProps?.onClick?.(e);
                                    }}
                                    value={subItem.value ?? subItem.text}
                                    active={subItem.active}
                                    data-testid={`menu-item-${kebabCase(subItem.text)}`}
                                    key={`${key}-${index}`}
                                    {...subItem.menuItemProps}
                                >
                                    <Highlight size={ROW_LABEL_SIZE} color="blue" highlight={internalHighlightedText}>
                                        {subItem.text}
                                    </Highlight>
                                </Menu.Item>
                            );
                        });
                        // Add a horizontal line after the group unless it's the last group
                        const lastGroupIndex = (() => {
                            for (let i = items.length - 1; i >= 0; i--) {
                                const maybeGroup = items[i];
                                if (
                                    !isValidElement(maybeGroup) &&
                                    "items" in maybeGroup &&
                                    typeof maybeGroup.items !== "undefined"
                                )
                                    return i;
                            }
                            return -1;
                        })();
                        if (typeof index === "number" && index !== lastGroupIndex) {
                            children.push(<Menu.Divider key={`${key}-divider`} />);
                        }
                        return children;
                    }
                    const buildedItems = item.items
                        .map((subItem, index) => {
                            return buildMenu(subItem, `subItem-${index}`);
                        })
                        .filter((subItem) => subItem !== null);
                    const subMenuId = `${key}-${kebabCase(item.text)}`;
                    return buildedItems.length > 0 ? (
                        <Menu.Sub
                            key={key}
                            floatingStrategy={subMenuFloatingStrategy}
                            // Only while held: otherwise Mantine's own hover and keyboard handling opens and closes it.
                            {...(heldSubMenu === subMenuId && { opened: true })}
                        >
                            <Menu.Sub.Target>
                                <Menu.Sub.Item
                                    onMouseLeave={(event: React.MouseEvent<HTMLElement>) =>
                                        holdSubMenuAcrossScrollbar(subMenuId, event)
                                    }
                                    leftSection={item.icon && <Icon icon={item.icon} />}
                                    disabled={item.disabled}
                                    data-testid={`menu-sub-item-${kebabCase(item.text)}`}
                                    active={item.active}
                                >
                                    <Highlight size={ROW_LABEL_SIZE} color="blue" highlight={internalHighlightedText}>
                                        {item.text}
                                    </Highlight>
                                </Menu.Sub.Item>
                            </Menu.Sub.Target>
                            <Menu.Sub.Dropdown
                                data-testid={`menu-sub-dropdown-${kebabCase(item.text)}`}
                                mah={maxSubMenuHeight}
                            >
                                {buildedItems}
                            </Menu.Sub.Dropdown>
                        </Menu.Sub>
                    ) : null;
                } else if ("text" in item) {
                    return item.text.toLowerCase().includes(search.toLowerCase()) ? (
                        <Menu.Item
                            key={key}
                            onClick={(e) => {
                                onClick?.(item.value ?? item.text);
                                item.menuItemProps?.onClick?.(e);
                            }}
                            value={item.value ?? item.text}
                            leftSection={item.icon && <Icon icon={item.icon} />}
                            active={item.active}
                            data-testid={`menu-item-${kebabCase(item.text)}`}
                            {...item.menuItemProps}
                        >
                            <Highlight size={ROW_LABEL_SIZE} color="blue" highlight={internalHighlightedText}>
                                {item.text}
                            </Highlight>
                        </Menu.Item>
                    ) : null;
                }
            },
            [
                onClick,
                search,
                groupType,
                items,
                subMenuFloatingStrategy,
                maxMenuHeight,
                maxSubMenuHeight,
                heldSubMenu,
                holdSubMenuAcrossScrollbar,
            ]
        );

        const builtMenuItems = useMemo(() => {
            return items.map((item, key) => buildMenu(item, `item-${key}`, key));
        }, [items, buildMenu]);

        const hasVisibleItems = builtMenuItems.some((item) => item !== null);

        // A searchable list keeps the height of its unfiltered rows while a search filters them, scrolling what no
        // longer fits, so the menu doesn't jump in size as the user types. It's a flex basis rather than a minimum
        // height, so on a short window the list still gives up height for the whole menu to fit the room.
        const listFlex = searchable
            ? `0 1 ${getRowsHeight(Math.min(items.length, SEARCHABLE_MENU_VISIBLE_ROWS))}`
            : undefined;

        // Inside the list, so a search that matches nothing keeps the list's height rather than adding to it.
        const noItemsMessage = !hasVisibleItems && (searchable || hasCustomSearch) && (
            <Text size="sm" dimmed ta="center" fs="italic" py="lg">
                {typeof noItemsFoundLabel === "undefined" ? "No items found" : noItemsFoundLabel}
            </Text>
        );

        const content = (
            <>
                {typeof header !== "undefined" && <Menu.Header>{header}</Menu.Header>}
                {searchable && (
                    <Menu.Search
                        inputProps={{
                            ref: inputRef,
                            placeholder: searchPlaceholder,
                            value: search,
                            onChange: (event) => setSearch(event.currentTarget.value),
                            "data-autofocus": true,
                        }}
                    />
                )}
                {(title || subtitle) && <Menu.Info title={title} subtitle={subtitle} />}
                {typeof maxMenuHeight !== "undefined" ? (
                    <ScrollArea.Autosize mah={maxMenuHeight} flex={listFlex}>
                        {builtMenuItems}
                        {noItemsMessage}
                    </ScrollArea.Autosize>
                ) : (
                    <>
                        {builtMenuItems}
                        {noItemsMessage}
                    </>
                )}
            </>
        );

        return (
            <Menu
                onExitTransitionEnd={() => {
                    inputRef.current?.blur(); // Blur the input when the menu closes
                    // The scrollbar the hold waits on is gone with the dropdown, so it would never be released.
                    setHeldSubMenu(null);
                }}
                onEnterTransitionEnd={() => {
                    inputRef.current?.focus(); // Focus the input when the menu opens
                }}
                onChange={onChange}
                onClose={() => {
                    setSearch("");
                }}
                opened={opened}
                // Mantine's own focus trap otherwise focuses an internal placeholder on mount, and moving focus
                // to the search from there is left to `onEnterTransitionEnd` above, which runs on a delay this
                // seed shouldn't wait out. Turning the placeholder off (only while there is something to seed,
                // so Explore keeps today's placeholder-then-`onEnterTransitionEnd` behaviour) lets the trap find
                // the search input's own `data-autofocus` below instead, and focus it directly on every mount.
                // `other` still wins when the consumer sets this explicitly.
                withInitialFocusPlaceholder={typeof initialSearch === "undefined" ? undefined : false}
                {...other}
            >
                <Menu.Target>{children}</Menu.Target>
                <Menu.Dropdown
                    ref={ref}
                    data-testid={dataTestId ?? "auto-menu-dropdown"}
                    onKeyDown={handleTypeToSearch}
                >
                    {typeof maxMenuHeight === "undefined" ? (
                        content
                    ) : (
                        <Stack gap={0} mah={MENU_AVAILABLE_HEIGHT}>
                            {content}
                        </Stack>
                    )}
                </Menu.Dropdown>
            </Menu>
        );
    }
);

/**
 * AutoMenu component that accepts plain items and transforms them using getter functions.
 * @template Item - The type of items in the array.
 * @param {Array<Item>} props.items - Array of plain items to transform into menu items.
 * @param {function} props.getSection - Function to get section label from an item.
 * @param {function} props.getLabel - Function to get label text from an item.
 * @param {function} props.getValue - Function to get value from an item.
 * @param {function} props.getGroup - Function to get group name from an item.
 * @param {function} props.getGroupOrder - Function to get group order from an item.
 * @param {function} props.getGroupItemOrder - Function to get item order within a group.
 * @param {function} props.getIcon - Function to get icon from an item.
 * @param {function} props.getItemOrder - Function to get item order.
 * @param {function} props.getGroupIcon - Function to get icon for a group.
 * @param {function} props.getActive - Function to determine if an item is active.
 * @param {boolean} props.moveActiveItemToTop - Whether to move active items to the top of the list.
 * @param {string | number} props.maxMenuHeight - Maximum height for the menu.
 * @param {string | number} props.maxSubMenuHeight - Maximum height for the category sub-menus.
 * @returns {JSX.Element} The AutoMenuWithPlainItems component.
 */
export const AutoMenuWithPlainItems = <Item,>({
    items,
    getSection,
    getLabel,
    getValue,
    getGroup,
    getGroupOrder,
    getGroupItemOrder,
    getIcon,
    getItemOrder,
    getGroupIcon,
    getActive,
    moveActiveItemToTop = false,
    ...other
}: IAutoMenuWithPlainItemsProps<Item>) => {
    const transformedItems = useMemo<Array<IAutoMenuElement>>(() => {
        if (!items || items.length === 0) {
            return [];
        }

        // Transform the list of Items into a list of our custom object with the props:
        // text, icon, itemOrder, group, groupOrder, groupItemOrder
        const transformedItems: Array<{
            text: string;
            value: string | number;
            icon?: IconDefinition;
            itemOrder?: number;
            group?: string;
            groupOrder?: number;
            groupItemOrder?: number;
            active?: boolean;
        }> = items.map((item) => {
            return {
                active: getActive?.(item),
                text: getLabel(item),
                value: getValue(item),
                icon: getIcon?.(item),
                itemOrder: getItemOrder?.(item),
                group: getGroup?.(item),
                groupOrder: getGroupOrder?.(item),
                groupItemOrder: getGroupItemOrder?.(item),
            };
        });

        // Reorder every item, using the orders, and remove tthe props:
        // itemOrder, groupOrder and groupItemOrder from each item (asuming they will be already ordered)
        const orderedTransformedItems: Array<{
            text: string;
            value: string | number;
            icon?: IconDefinition;
            itemOrder?: number;
            group?: string;
            groupOrder?: number;
            groupItemOrder?: number;
            active?: boolean;
        }> = transformedItems
            .sort((itemOne, itemTwo) => itemOne.text.localeCompare(itemTwo.text))
            .sort((itemOne, itemTwo) =>
                typeof itemOne.itemOrder !== "undefined" && typeof itemTwo.itemOrder !== "undefined"
                    ? itemOne.itemOrder - itemTwo.itemOrder
                    : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group if any
                typeof itemOne.group !== "undefined" && typeof itemTwo.group !== "undefined"
                    ? itemOne.group.localeCompare(itemTwo.group)
                    : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group order if exists
                typeof itemOne.groupOrder !== "undefined" && typeof itemTwo.groupOrder !== "undefined"
                    ? itemOne.groupOrder - itemTwo.groupOrder
                    : 0
            )
            .sort((itemOne, itemTwo) => {
                if (typeof itemOne.group !== "undefined" && itemOne.group === itemTwo.group) {
                    // Sort by group item order if exists
                    return typeof itemOne.groupItemOrder !== "undefined" &&
                        typeof itemTwo.groupItemOrder !== "undefined"
                        ? itemOne.groupItemOrder - itemTwo.groupItemOrder
                        : 0;
                }
                return 0;
            })
            .map(({ itemOrder, groupOrder, groupItemOrder, ...rest }) => {
                return rest;
            });

        // Group the items by group, using the group prop, and remove the group prop from each item
        const groupedItems: Array<IAutoMenuElement> = orderedTransformedItems.reduce((acc, item) => {
            const { group, ...itemWithoutGroup } = item;
            if (group) {
                const existingGroup: IAutoMenuItem | undefined = acc.find(
                    (accItem) => "text" in accItem && "items" in accItem && accItem.text === group
                ) as IAutoMenuItem;

                if (existingGroup) {
                    if (existingGroup.items) {
                        existingGroup.items.push(itemWithoutGroup);
                        // If this child item is active, mark the parent group as active
                        if (item.active && !existingGroup.active && !moveActiveItemToTop) {
                            existingGroup.active = true;
                        }
                    } else {
                        existingGroup.items = [itemWithoutGroup];
                        if (item.active && !moveActiveItemToTop) {
                            existingGroup.active = true;
                        }
                    }
                } else {
                    acc.push({
                        text: group,
                        value: item.value,
                        icon: getGroupIcon?.(group),
                        items: [itemWithoutGroup],
                        ...(item.active && !moveActiveItemToTop && { active: true }),
                    });
                }
            } else {
                acc.push({
                    text: item.text,
                    value: item.value,
                    icon: item.icon,
                    ...(item.active && { active: item.active }),
                } as IAutoMenuItem);
            }
            return acc;
        }, [] as Array<IAutoMenuElement>);

        // Move the active item to the top of the list
        if (moveActiveItemToTop) {
            let activeItem: IAutoMenuItem | null = null;
            const modifiedGroups: Array<IAutoMenuElement> = [];

            for (const item of groupedItems) {
                if ("items" in item && item.items) {
                    const activeItemInGroup = item.items.find((subItem) => subItem.active);

                    // If there is an active item in the group, create it as an item without the group so it can be displayed at the top of the list
                    if (activeItemInGroup && !activeItem) {
                        activeItem = {
                            text: activeItemInGroup.text,
                            value: activeItemInGroup.value,
                            icon: activeItemInGroup.icon,
                            active: true,
                        };

                        // Remove the active item from the parent group's items
                        const remainingItems = item.items.filter(
                            (subItem) => subItem.value !== activeItemInGroup.value || !subItem.active
                        );

                        // Only add the parent group if it has remaining items
                        if (remainingItems.length > 0) {
                            modifiedGroups.push({
                                text: item.text,
                                value: item.value,
                                icon: item.icon,
                                items: remainingItems,
                                active: false,
                            });
                        }
                    } else {
                        modifiedGroups.push(item);
                    }
                } else if ("active" in item && item.active && !activeItem) {
                    activeItem = item;
                } else {
                    modifiedGroups.push(item);
                }
            }

            return activeItem ? [activeItem, ...modifiedGroups] : modifiedGroups;
        }

        return groupedItems;
    }, [
        items,
        moveActiveItemToTop,
        getActive,
        getLabel,
        getValue,
        getIcon,
        getItemOrder,
        getGroup,
        getGroupOrder,
        getGroupItemOrder,
        getGroupIcon,
    ]);

    return <AutoMenu items={transformedItems} {...other} />;
};

AutoMenu.WithPlainItems = AutoMenuWithPlainItems;

export { AutoMenu };
